import { motion } from 'framer-motion';
import type { CardPayload, RiskKind } from '@lumlix/core/events';

/* Tool output renders as typed cards, never raw JSON. A campaign plan is a document the user
   reads and approves; a payload diff is the last thing between them and spending money. Both
   earn real layout. */

const RISK_COLOR: Record<RiskKind, string> = {
  spend: 'var(--lx-risk-spend)',
  publish: 'var(--lx-risk-publish)',
  change: 'var(--lx-risk-change)',
};

const enter = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
};

function Shell({
  title,
  accent,
  right,
  children,
}: {
  title: string;
  accent?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <motion.section className="lx-card" {...enter} style={accent ? { borderColor: accent } : undefined}>
      <header className="lx-card-head">
        {accent && (
          <span style={{ width: 7, height: 7, borderRadius: 99, background: accent, flexShrink: 0 }} />
        )}
        <span className="lx-eyebrow">{title}</span>
        {right}
      </header>
      <div className="lx-card-body">{children}</div>
    </motion.section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="lx-kv">
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function Pill({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <span className="lx-pill" style={color ? { borderColor: color, color } : undefined}>
      {children}
    </span>
  );
}

function money(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function PlanCard({ plan }: { plan: Record<string, unknown> }) {
  const p = plan as {
    goal?: string;
    objective?: string;
    channelMix?: string[];
    audiences?: Array<{ name: string }>;
    messagingAngles?: string[];
    budget?: Array<{ platform: string; dailyBudgetCents: number }>;
    successCriteria?: Array<{ metric: string; target: number; unit: string }>;
    assumptions?: string[];
  };
  const daily = (p.budget ?? []).reduce((s, b) => s + (b.dailyBudgetCents ?? 0), 0);
  return (
    <Shell title="Campaign plan">
      <div style={{ fontSize: 16.5, fontWeight: 650, marginBottom: 'var(--lx-s-3)', letterSpacing: '-0.015em' }}>
        {p.goal}
      </div>
      <Row label="Objective">{p.objective}</Row>
      <Row label="Channels">{(p.channelMix ?? []).map((c) => <Pill key={c}>{c}</Pill>)}</Row>
      <Row label="Audiences">{(p.audiences ?? []).map((a) => <Pill key={a.name}>{a.name}</Pill>)}</Row>
      <Row label="Angles">
        <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--lx-text-2)' }}>
          {(p.messagingAngles ?? []).map((m) => <li key={m}>{m}</li>)}
        </ul>
      </Row>
      <Row label="Daily budget">
        <strong style={{ color: 'var(--lx-risk-spend)', fontSize: 15 }}>{money(daily)}</strong>
        <span className="lx-muted"> across {(p.budget ?? []).length} platforms</span>
      </Row>
      <Row label="Success">
        {(p.successCriteria ?? []).map((s) => (
          <Pill key={s.metric}>{s.metric} → {s.target}{s.unit}</Pill>
        ))}
      </Row>
      {!!p.assumptions?.length && (
        <Row label="Assumptions">
          <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--lx-text-3)', fontSize: 12.5 }}>
            {p.assumptions.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </Row>
      )}
    </Shell>
  );
}

function CreativesCard({ variants }: { variants: unknown[] }) {
  const vs = variants as Array<{
    platform: string; headline: string; primaryText: string; cta: string; angle: string;
  }>;
  return (
    <Shell title={`Creative variants · ${vs.length}`}>
      <div style={{ display: 'grid', gap: 'var(--lx-s-3)' }}>
        {vs.map((v, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            style={{
              border: '1px solid var(--lx-line)',
              borderRadius: 'var(--lx-r)',
              padding: 'var(--lx-s-3) var(--lx-s-4)',
              background: 'var(--lx-surface-2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Pill>{v.platform}</Pill>
              <span style={{ fontSize: 11.5, color: 'var(--lx-text-3)' }}>{v.angle}</span>
            </div>
            <div style={{ fontWeight: 650, marginBottom: 4 }}>{v.headline}</div>
            <div style={{ color: 'var(--lx-text-2)', fontSize: 13.5 }}>{v.primaryText}</div>
            <div style={{ marginTop: 10 }}><Pill color="var(--lx-violet-2)">{v.cta}</Pill></div>
          </motion.div>
        ))}
      </div>
    </Shell>
  );
}

/** Renders the exact payload we would send. This ships before any platform approval lands,
    which is what makes Dry-Run a real product rather than a stub. */
function PayloadDiffCard({
  platform,
  after,
  warnings,
}: {
  platform: string;
  after: unknown;
  warnings: string[];
}) {
  return (
    <Shell title={`${platform} · payload preview`}>
      {warnings.length > 0 && (
        <div
          style={{
            border: '1px solid color-mix(in srgb, var(--lx-warn) 55%, transparent)',
            background: 'color-mix(in srgb, var(--lx-warn) 10%, transparent)',
            borderRadius: 'var(--lx-r-sm)',
            padding: 'var(--lx-s-3)',
            marginBottom: 'var(--lx-s-3)',
            fontSize: 13.5,
          }}
        >
          <strong style={{ color: 'var(--lx-warn)' }}>
            {warnings.length} thing{warnings.length > 1 ? 's' : ''} the platform can’t express
          </strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: 'var(--lx-text-2)' }}>
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}
      <pre className="lx-code lx-scroll">{JSON.stringify(after, null, 2)}</pre>
    </Shell>
  );
}

function ApprovalCard({
  title,
  rows,
  onDecide,
  pending,
}: {
  title: string;
  rows: Array<{ label: string; value: string; risk?: RiskKind }>;
  onDecide?: (d: 'approve' | 'reject') => void;
  pending?: boolean;
}) {
  const topRisk = rows.find((r) => r.risk)?.risk;
  const accent = topRisk ? RISK_COLOR[topRisk] : 'var(--lx-violet-2)';
  return (
    <Shell title="Approval required" accent={accent}>
      <div style={{ fontSize: 15.5, fontWeight: 650, marginBottom: 'var(--lx-s-3)' }}>{title}</div>
      {rows.map((r) => (
        <div className="lx-approve-row" key={r.label}>
          <span className="lx-muted" style={{ fontSize: 13 }}>{r.label}</span>
          <span
            style={{
              color: r.risk ? RISK_COLOR[r.risk] : 'var(--lx-text)',
              fontWeight: r.risk ? 650 : 400,
              textAlign: 'right',
              overflowWrap: 'anywhere',
            }}
          >
            {r.value}
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 'var(--lx-s-2)', marginTop: 'var(--lx-s-4)' }}>
        <button
          className="lx-btn lx-btn-primary lx-focus"
          disabled={pending}
          onClick={() => onDecide?.('approve')}
          style={{ flex: 1, opacity: pending ? 0.6 : 1, cursor: pending ? 'wait' : 'pointer' }}
        >
          Approve
        </button>
        <button
          className="lx-btn lx-focus"
          disabled={pending}
          onClick={() => onDecide?.('reject')}
          style={{ background: 'transparent', cursor: pending ? 'wait' : 'pointer' }}
        >
          Reject
        </button>
      </div>
    </Shell>
  );
}

function MetricsCard({
  range,
  series,
}: {
  range: string;
  series: Array<{ label: string; points: Array<[string, number]> }>;
}) {
  return (
    <Shell title={`Performance · ${range}`}>
      <div style={{ display: 'grid', gap: 'var(--lx-s-5)' }}>
        {series.map((s) => {
          const vals = s.points.map(([, v]) => v);
          const max = Math.max(1, ...vals);
          const last = vals.at(-1) ?? 0;
          return (
            <div key={s.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <span className="lx-muted" style={{ fontSize: 12.5 }}>{s.label}</span>
                <strong style={{ fontSize: 17, letterSpacing: '-0.02em' }}>{last.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 48 }}>
                {s.points.map(([t, v], i) => (
                  <motion.div
                    key={t}
                    title={`${t}: ${v}`}
                    initial={{ height: 0 }}
                    animate={{ height: `${Math.max(3, (v / max) * 100)}%` }}
                    transition={{ duration: 0.5, delay: i * 0.02, ease: [0.22, 1, 0.36, 1] }}
                    style={{ flex: 1, background: 'var(--lx-grad)', borderRadius: 3, opacity: 0.9 }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

function ContentCard({
  items,
}: {
  items: Array<{ id: string; title: string; status: string; scheduledAt?: string }>;
}) {
  return (
    <Shell title={`Content · ${items.length}`}>
      <div style={{ display: 'grid', gap: 6 }}>
        {items.map((it) => (
          <div
            key={it.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--lx-s-3)',
              padding: '9px var(--lx-s-3)',
              border: '1px solid var(--lx-line)',
              borderRadius: 'var(--lx-r-sm)',
              background: 'var(--lx-surface-2)',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.title}
            </span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
              {it.scheduledAt && (
                <span style={{ fontSize: 11.5, color: 'var(--lx-text-3)' }}>
                  {new Date(it.scheduledAt).toLocaleString()}
                </span>
              )}
              <Pill color={it.status === 'published' ? 'var(--lx-ok)' : undefined}>{it.status}</Pill>
            </span>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function Card({
  card,
  onDecide,
  pending,
}: {
  card: CardPayload;
  onDecide?: (approvalId: string, d: 'approve' | 'reject') => void;
  pending?: boolean;
}) {
  switch (card.kind) {
    case 'plan':
      return <PlanCard plan={card.plan as Record<string, unknown>} />;
    case 'creatives':
      return <CreativesCard variants={card.variants} />;
    case 'payload_diff':
      return <PayloadDiffCard platform={card.platform} after={card.after} warnings={card.warnings} />;
    case 'approval':
      return (
        <ApprovalCard
          title={card.title}
          rows={card.rows}
          pending={pending}
          onDecide={(d) => onDecide?.(card.approvalId, d)}
        />
      );
    case 'metrics':
      return <MetricsCard range={card.range} series={card.series} />;
    case 'content':
      return <ContentCard items={card.items} />;
  }
}
